/**
 * 报表导出服务
 *
 * 【DDD 迁移】本服务当前指向 /reports（旧路由）
 * 待 DDD 迁移完成后，应改为指向 /reports-new
 * 迁移步骤：1. 启用 FF_REPORTS_DDD=true；2. 验证 DDD 报表功能；3. 修改 baseURL
 */
import api from './api';

export class ReportService {
  /**
   * 获取认证Token
   */
  private static getAuthToken(): string | null {
    try {
      const authStorage = localStorage.getItem('auth-storage');
      if (authStorage) {
        const authState = JSON.parse(authStorage);
        return authState?.state?.token || null;
      }
    } catch (error) {
      console.error('获取认证token失败:', error);
    }
    return null;
  }

  /**
   * 导出项目Excel报表
   */
  static async exportProjectExcel(
    projectId: string,
    startDate?: string,
    endDate?: string,
    constructionType?: string
  ): Promise<Blob> {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (constructionType) params.constructionType = constructionType;

    const token = this.getAuthToken();
    if (!token) {
      throw new Error('未登录或登录已过期，请重新登录');
    }

    const response = await fetch(
      `${api.defaults.baseURL}/reports/project/${projectId}/excel?${new URLSearchParams(params)}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('认证失败，请重新登录');
      }
      throw new Error('导出Excel报表失败');
    }

    return await response.blob();
  }

  /**
   * 导出项目PDF报表
   */
  static async exportProjectPDF(
    projectId: string,
    startDate?: string,
    endDate?: string,
    constructionType?: string
  ): Promise<Blob> {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (constructionType) params.constructionType = constructionType;

    const token = this.getAuthToken();
    if (!token) {
      throw new Error('未登录或登录已过期，请重新登录');
    }

    const response = await fetch(
      `${api.defaults.baseURL}/reports/project/${projectId}/pdf?${new URLSearchParams(params)}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('认证失败，请重新登录');
      }
      throw new Error('导出PDF报表失败');
    }

    return await response.blob();
  }

  /**
   * 下载文件
   */
  static downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * 导出并下载Excel报表
   */
  static async exportAndDownloadExcel(
    projectId: string,
    projectName: string,
    startDate?: string,
    endDate?: string,
    constructionType?: string
  ): Promise<void> {
    const blob = await this.exportProjectExcel(projectId, startDate, endDate, constructionType);
    const filename = `${projectName}-报表-${new Date().getTime()}.xlsx`;
    this.downloadFile(blob, filename);
  }

  /**
   * 导出并下载PDF报表
   */
  static async exportAndDownloadPDF(
    projectId: string,
    projectName: string,
    startDate?: string,
    endDate?: string,
    constructionType?: string
  ): Promise<void> {
    const blob = await this.exportProjectPDF(projectId, startDate, endDate, constructionType);
    const filename = `${projectName}-报表-${new Date().getTime()}.pdf`;
    this.downloadFile(blob, filename);
  }
}
